/**
 * Centralized authentication error handler.
 * Handles 401/403 errors globally and manages logout/redirect logic.
 */
import { store } from '../redux/store';
import { logout, setMustChangePassword } from '../redux/slices/auth.slice';

/** Matches backend MUST_CHANGE_PASSWORD_MESSAGE (JwtAuthGuard / MustChangePasswordGuard). */
export const MUST_CHANGE_PASSWORD_MESSAGE_FRAGMENT =
  'Establezca una nueva contraseña para continuar';

let navigateFunction: ((path: string) => void) | null = null;
let isHandlingUnauthorized = false;
let isHandlingMustChangePassword = false;

export const setNavigateFunction = (navigate: (path: string) => void): void => {
  navigateFunction = navigate;
};

/** Reset redirect guards after a successful login (new session). */
export const resetAuthErrorHandlingState = (): void => {
  isHandlingUnauthorized = false;
  isHandlingMustChangePassword = false;
};

export const handleUnauthorizedError = (): void => {
  if (isHandlingUnauthorized) {
    return;
  }
  isHandlingUnauthorized = true;

  store.dispatch(logout());

  if (navigateFunction != null) {
    navigateFunction('/login');
  } else if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

export const handleMustChangePasswordError = (): void => {
  if (isHandlingMustChangePassword) {
    return;
  }
  isHandlingMustChangePassword = true;

  store.dispatch(setMustChangePassword(true));
  if (navigateFunction != null) {
    navigateFunction('/force-change-password');
  } else if (window.location.pathname !== '/force-change-password') {
    window.location.href = '/force-change-password';
  }
};

function messageFromUnknown(error: unknown): string | null {
  if (error == null || typeof error !== 'object') {
    return null;
  }
  const withBody = error as { body?: unknown; message?: unknown };
  if (withBody.body != null && typeof withBody.body === 'object') {
    const bodyMessage = (withBody.body as { message?: unknown }).message;
    if (typeof bodyMessage === 'string') {
      return bodyMessage;
    }
  }
  if (typeof withBody.message === 'string') {
    return withBody.message;
  }
  return null;
}

/**
 * Check if an API error is a "must change password" 403.
 */
export const isMustChangePasswordError = (error: unknown): boolean => {
  const message = messageFromUnknown(error);
  return message != null && message.includes(MUST_CHANGE_PASSWORD_MESSAGE_FRAGMENT);
};

export const isMustChangePasswordMessage = (message: string): boolean => {
  return message.includes(MUST_CHANGE_PASSWORD_MESSAGE_FRAGMENT);
};
